import { defineMutation, useMutation, useQueryCache } from '@pinia/colada';
import { storeToRefs } from 'pinia';

import { type InsightInteractionType, recordInsightInteraction } from '@/apis/insights';
import { QueryKeys } from '@/shared/queries/keys';
import { useIdentityStore } from '@/stores/identity';

interface RecordInteractionVars {
  insightId: number;
  firstViewedAt?: string;
  interactionType?: InsightInteractionType;
}

// brandId is captured at mutation-invoke time and threaded through vars so
// onSettled always invalidates the brand the interaction was recorded against,
// even if the user switches brands while the POST is in flight.
interface InternalRecordInteractionVars extends RecordInteractionVars {
  brandId: number | undefined;
}

export const useRecordInsightInteraction = defineMutation(() => {
  const { currentBrand } = storeToRefs(useIdentityStore());
  const queryCache = useQueryCache();

  const { mutate, mutateAsync, ...mutation } = useMutation({
    async mutation({ insightId, firstViewedAt, interactionType }: InternalRecordInteractionVars) {
      const response = await recordInsightInteraction({
        insightId,
        firstViewedAt,
        interactionType,
      });
      return response.data;
    },
    onSettled(_data, _error, vars) {
      if (!vars.interactionType) return;
      void queryCache.invalidateQueries({
        key: QueryKeys.insights.list(vars.brandId ? [vars.brandId] : []),
      });
    },
  });

  function recordInteraction(vars: RecordInteractionVars) {
    return mutate({ ...vars, brandId: currentBrand.value?.id });
  }
  function recordInteractionAsync(vars: RecordInteractionVars) {
    return mutateAsync({ ...vars, brandId: currentBrand.value?.id });
  }

  return { recordInteraction, recordInteractionAsync, ...mutation };
});
